'use client'

import React, { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { CircleAlert, Check, Upload } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button, ButtonLink, cn } from '@kiao-voice/ui-kit'
import { AUDIO_BATCH_ALLOWED_EXTENSIONS } from '@kiao-voice/core'
import { ProgressLine } from '../batch-transcription/progress-line'
import { transcriptionFailureMessage } from '../transcription-failure'
import {
  TRIAL_MAX_MB_FALLBACK,
  TRIAL_TRIM_SECS_FALLBACK,
  type TrialPanelView,
  type TrialState,
} from '../anonymous-trial/trial-flow'
import { useAnonymousTrialFlow } from '../anonymous-trial/use-anonymous-trial-flow'
import { trackSiteLinkClicked } from '../../lib/analytics'

/**
 * 副檔名列舉，不是複合 wildcard。Safari 對 `audio/*,video/*` 這種組合會把
 * 使用者的檔案在選取視窗裡整排變灰（memory #778 的同一類陷阱）。
 */
const ACCEPT_EXTENSIONS = AUDIO_BATCH_ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(',')

/**
 * 試轉框。同一個框原地換內容，最小高度固定，換狀態時下面的版面不跳。
 *
 * 三個面板（閒置／進行／失敗）**同時存在於 DOM**，用 `hidden` 切換而不是條件
 * 渲染：進行中的面板一旦被卸載再掛回來，`ProgressLine` 的 CSS 過渡會從頭開始，
 * 線就不再連續。只有閒置狀態有虛線框——上傳開始後已經不能再拖檔案進來，框線
 * 沒有意義。
 */
export function TrialUploader() {
  const { t } = useTranslation(['web'])
  const { state, view, chooseFile, notifyPickerOpened, cancel, reset } = useAnonymousTrialFlow()
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [over, setOver] = useState(false)

  const idle = view.kind === 'idle'

  return (
    <div
      data-testid="trial-dropzone"
      aria-label={t('web:transcriptionPage.trial.dropzoneAria')}
      onDragEnter={(event) => {
        event.preventDefault()
        if (idle) setOver(true)
      }}
      onDragOver={(event) => {
        event.preventDefault()
        if (idle) setOver(true)
      }}
      onDragLeave={(event) => {
        event.preventDefault()
        setOver(false)
      }}
      onDrop={(event) => {
        event.preventDefault()
        setOver(false)
        const file = event.dataTransfer?.files?.[0]
        if (file && idle) chooseFile(file)
      }}
      className={cn(
        'flex min-h-[280px] flex-col rounded-(--radius) transition-colors',
        idle
          ? 'items-center justify-center gap-2.5 border-[1.5px] border-dashed px-5 py-[clamp(24px,4vw,44px)] text-center'
          : 'items-start justify-center border-[1.5px] border-dashed border-transparent',
        idle && (over ? 'border-foreground bg-fg-soft' : 'border-[color-mix(in_oklch,var(--fg)_28%,transparent)]')
      )}
    >
      <input
        ref={inputRef}
        data-testid="trial-file-input"
        type="file"
        accept={ACCEPT_EXTENSIONS}
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0]
          // 同一個檔案連選兩次也要觸發，所以選完就清空。
          event.target.value = ''
          if (file) chooseFile(file)
        }}
      />

      <IdlePanel
        hidden={!idle}
        onChoose={() => {
          notifyPickerOpened('button')
          inputRef.current?.click()
        }}
      />
      <RunPanel hidden={view.kind !== 'run'} view={view} onCancel={cancel} />
      <ErrorPanel hidden={view.kind !== 'error'} view={view} state={state} onReset={reset} />
    </div>
  )
}

function IdlePanel({ hidden, onChoose }: { hidden: boolean; onChoose: () => void }) {
  const { t } = useTranslation(['web'])
  return (
    <div hidden={hidden} className="flex w-full flex-col items-center gap-2.5 text-center">
      <span aria-hidden="true" className="text-foreground">
        <Upload className="size-7" strokeWidth={1.8} />
      </span>
      <p className="text-lg font-semibold">{t('web:transcriptionPage.trial.idleTitle')}</p>
      <p className="mb-2 font-mono text-(length:--fs-meta) leading-[1.7] text-muted">
        {t('web:transcriptionPage.trial.dropSpec', {
          minutes: TRIAL_TRIM_SECS_FALLBACK / 60,
          maxMb: TRIAL_MAX_MB_FALLBACK,
        })}
        <br />
        {t('web:transcriptionPage.trial.dropFormats')}
      </p>
      <Button variant="primary" size="marketing" onClick={onChoose}>
        {t('web:transcriptionPage.trial.chooseFile')}
      </Button>
    </div>
  )
}

function RunPanel({
  hidden,
  view,
  onCancel,
}: {
  hidden: boolean
  view: TrialPanelView
  onCancel: () => void
}) {
  const { t } = useTranslation(['web'])
  const title = useStableTitle(view)

  return (
    <div hidden={hidden} data-testid="trial-panel" className="w-full max-w-[400px]">
      <div className="flex items-baseline justify-between gap-3">
        {/*
          只在 titleKey 變的時候才換字（見 useStableTitle）：這一塊每 250ms
          重算一次，`aria-live` 若跟著百分比走，螢幕閱讀器會不停地念數字。
        */}
        <p data-testid="trial-title" role="status" aria-live="polite" className="text-md font-semibold">
          {title}
        </p>
        <span data-testid="trial-percent" className="font-mono text-sm text-muted">
          {view.done ? (
            <Check aria-hidden="true" className="size-[15px] text-foreground" strokeWidth={2.5} />
          ) : view.percent === null ? null : (
            `${view.percent}%`
          )}
        </span>
      </div>

      <ProgressLine
        thick
        className="mt-3"
        percent={view.percent}
        indeterminate={view.indeterminate}
        done={view.done}
        label={t('web:transcriptionPage.trial.progressAria')}
      />

      {view.fileName ? (
        <p className="mt-4 text-sm text-foreground">
          {view.fileName}
          {view.fileMetaKey ? (
            <span className="text-muted"> · {t(`web:${view.fileMetaKey}`, view.fileMetaParams)}</span>
          ) : null}
        </p>
      ) : null}
      {view.noteKey ? (
        <p className="mt-2.5 text-sm leading-[1.6] text-muted">{t(`web:${view.noteKey}`)}</p>
      ) : null}
      {/*
        上傳期間才出現：iOS 把分頁丟到背景會砍掉進行中的連線，使用者切去別的
        App 再回來就是上傳失敗。轉錄階段沒有這個問題，所以那時不顯示。
      */}
      {view.holdKey ? (
        <p data-testid="trial-stay-hint" className="mt-2 text-sm leading-[1.6] text-muted">
          {t(`web:${view.holdKey}`)}
        </p>
      ) : null}

      <div hidden={!view.showCancel} className="mt-4">
        <Button data-testid="trial-cancel" variant="secondary" size="compact" onClick={onCancel}>
          {t('web:transcriptionPage.trial.cancel')}
        </Button>
      </div>
      <div hidden={!view.showSeeResult} className="mt-[18px]">
        {view.resultHref ? (
          <ButtonLink
            data-testid="trial-see-result"
            as={Link}
            href={view.resultHref}
            variant="primary"
            size="marketing"
          >
            {t('web:transcriptionPage.trial.seeResult')}
          </ButtonLink>
        ) : null}
      </div>
    </div>
  )
}

function ErrorPanel({
  hidden,
  view,
  state,
  onReset,
}: {
  hidden: boolean
  view: TrialPanelView
  state: TrialState
  onReset: () => void
}) {
  const { t } = useTranslation(['web'])
  const failure = state.kind === 'failed' ? state.failure : null
  const description =
    failure?.kind === 'transcription_failed'
      ? transcriptionFailureMessage(t, failure.errorCode ?? null)
      : view.errorDescKey
        ? t(`web:${view.errorDescKey}`, view.errorDescParams)
        : null

  return (
    <div hidden={hidden} data-testid="trial-error" className="w-full max-w-[400px]">
      <p className="inline-flex items-center gap-2 font-semibold text-error">
        <CircleAlert aria-hidden="true" className="size-4 flex-none" />
        {view.kind === 'error' ? t(`web:${view.titleKey}`, view.titleParams) : null}
      </p>
      {view.fileName ? <p className="mt-3 text-sm text-foreground">{view.fileName}</p> : null}
      {description ? (
        <p className="mt-2.5 text-sm leading-[1.6] text-muted">{description}</p>
      ) : null}
      <div className="mt-4 flex flex-wrap items-center gap-(--gap-sm)">
        <Button data-testid="trial-reset" variant="secondary" size="compact" onClick={onReset}>
          {t('web:transcriptionPage.trial.retry')}
        </Button>
        <ButtonLink
          as={Link}
          href="/signup"
          variant="primary"
          size="compact"
          onClick={() => trackSiteLinkClicked('try', '/signup')}
        >
          {t('web:transcriptionPage.trial.signUp')}
        </ButtonLink>
      </div>
    </div>
  )
}

/**
 * 標題只在 `titleKey`（階段）變的時候才換內容。
 *
 * 這一塊每 250ms 重算一次以推進百分比；標題若跟著重算，掛在它上面的
 * `aria-live` 會讓螢幕閱讀器每次都重念一遍。
 */
function useStableTitle(view: TrialPanelView): string {
  const { t, i18n } = useTranslation(['web'])
  const key = view.kind === 'run' ? view.titleKey : ''
  const sub = typeof view.titleParams?.sub === 'string' ? view.titleParams.sub : null
  // `sub` 走的是「階段 · 子階段」那條路，它的 key 不帶插值；其餘標題（裁切中
  // 要說出幾分鐘）得把參數傳給 `t`，否則畫面上會留下原封不動的 `{{minutes}}`。
  const params = sub === null && view.kind === 'run' ? view.titleParams : undefined
  const [title, setTitle] = useState('')
  const lastRef = useRef('')

  useEffect(() => {
    if (key === '') return
    // 語系也進 signature：切語言時 key 沒變，少了它標題會留在舊語言。
    const signature = `${i18n.language}|${key}|${sub ?? ''}|${JSON.stringify(params ?? null)}`
    if (signature === lastRef.current) return
    lastRef.current = signature
    setTitle(sub ? `${t(`web:${key}`)} · ${t(`web:${sub}`)}` : t(`web:${key}`, params))
  }, [key, sub, params, t, i18n.language])

  return title
}
